import React from "react";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { SplitButton } from "@design-system/Button/SplitButton";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import importerStyles from "@components/importer/ImporterPage.module.scss";
import {
    AccrualDraftReviewTaskItem,
    AccrualDraftReviewTasks,
} from "./AccrualDraftReviewTasks";

interface Props {
    reviewTasks: AccrualDraftReviewTaskItem[];
    onReviewTaskChange: (task: AccrualDraftReviewTaskItem | undefined) => void;
    onDownload: () => void;
    isDownloading: boolean;
    onDiscard: () => void;
    onConfirmSuggestions?: () => void;
    onConfirm: () => void;
    onConfirmAndClose: () => void;
    isConfirming: boolean;
    isConfirmDisabled: boolean;
    confirmDisabledReason: string | null;
}

export const AccrualDraftCommandBar: React.FC<Props> = ({
    reviewTasks,
    onReviewTaskChange,
    onDownload,
    isDownloading,
    onDiscard,
    onConfirmSuggestions,
    onConfirm,
    onConfirmAndClose,
    isConfirming,
    isConfirmDisabled,
    confirmDisabledReason,
}) => {
    useKeyboardCommand({
        keys: "mod+enter",
        callback: () => {
            onConfirm();
        },
        options: {
            description: "Confirm invoice",
            enabled: !isConfirmDisabled,
            enableOnFormTags: true,
            preventDefault: true,
        },
        dependencies: [onConfirm, isConfirmDisabled],
    });

    return (
        <CommandIsland className={importerStyles.previewCommandBar}>
            <AccrualDraftReviewTasks
                tasks={reviewTasks}
                onTaskChange={onReviewTaskChange}
            />
            <div className={importerStyles.commandBarActions}>
                <Button
                    variant="secondary"
                    size="md"
                    onClick={onDownload}
                    disabled={isDownloading}
                >
                    Download PDF
                </Button>
                <Button variant="secondary" size="md" onClick={onDiscard}>
                    Discard
                </Button>
                {onConfirmSuggestions ? (
                    <Button
                        variant="secondary"
                        size="md"
                        onClick={onConfirmSuggestions}
                    >
                        Confirm suggestions
                    </Button>
                ) : null}
                <Tooltip content={confirmDisabledReason}>
                    <SplitButton
                        variant="primary"
                        size="md"
                        onPrimaryClick={onConfirm}
                        disabled={isConfirmDisabled}
                        loading={isConfirming}
                        menuAriaLabel="More confirm options"
                        menu={
                            <Menu.Item
                                disabled={isConfirmDisabled}
                                disabledReason={confirmDisabledReason}
                                onClick={onConfirmAndClose}
                            >
                                Confirm and close
                            </Menu.Item>
                        }
                    >
                        Confirm <Shortcut keys="mod+enter" />
                    </SplitButton>
                </Tooltip>
            </div>
        </CommandIsland>
    );
};
